/**
 * Standalone, Keycloak-free OAuth callback bridge.
 *
 * An OAuth provider redirects the browser here after consent. This
 * runs in `main.tsx` BEFORE — and INSTEAD OF — the normal app bootstrap: no
 * Keycloak `login-required`, no React, no app bundle. A third-party redirect
 * landing must never depend on our own auth server being reachable — gating it
 * behind login-required previously coupled the final hop of a connection to
 * Keycloak's uptime, so a transient Keycloak 502 stranded an otherwise-completed
 * connection (the provider is already persisted server-side by this point).
 *
 * Its only job: relay the result to the original tab and close. Same-origin as
 * the app, so all three signalling channels in `oauth-bridge.ts` work. The
 * channel name, storage key and flush timing are imported from there so the
 * listener (`subscribeOAuthResult`) and this producer never drift.
 */

import {
  CLOSE_FALLBACK_MS,
  MESSAGE_FLUSH_MS,
  OAUTH_ACK_KEY,
  isOAuthAcknowledgement,
  oauthDestinationPath,
  openOAuthBroadcast,
  writeOAuthResultToStorage,
  type OAuthBridgeMessage,
} from './oauth-bridge'

/** Path the BE redirects the OAuth tab to (see integration-svc OAuthController). */
export const OAUTH_CALLBACK_PATH = '/oauth/callback'

/**
 * Relay the OAuth result to the opener and close. Reads the outcome from the
 * current URL's query string (the BE encodes status/ids there). Renders a
 * minimal in-place message — no design-system or React dependency.
 */
export function runOAuthCallbackBridge(
  navigateInPlace: (path: string) => void = (path) => window.location.assign(path),
): void {
  const params = new URLSearchParams(window.location.search)
  const msg: OAuthBridgeMessage = {
    status: params.get('status') === 'connected' ? 'connected' : 'error',
    oauthProviderId: params.get('oauthProviderId') ?? undefined,
    platformConnectorId: params.get('platformConnectorId') ?? undefined,
    requiresOrganisationReview:
      params.get('requiresOrganisationReview') == null
        ? undefined
        : params.get('requiresOrganisationReview') === 'true',
    reason: params.get('reason') ?? undefined,
  }

  const channel = openOAuthBroadcast()
  let closeTimer: number | undefined
  let acknowledged = false
  const closeAfterAcknowledgement = () => {
    if (acknowledged) return
    acknowledged = true
    if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer)
    window.removeEventListener('storage', onStorageAcknowledgement)
    closeTimer = window.setTimeout(() => {
      channel?.close()
      window.close()
    }, MESSAGE_FLUSH_MS)
  }
  const onStorageAcknowledgement = (event: StorageEvent) => {
    if (event.key !== OAUTH_ACK_KEY || !event.newValue) return
    try {
      if (isOAuthAcknowledgement(JSON.parse(event.newValue), msg)) {
        closeAfterAcknowledgement()
      }
    } catch {
      // Malformed or unrelated storage entry — ignore.
    }
  }
  if (channel) {
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (isOAuthAcknowledgement(event.data, msg)) closeAfterAcknowledgement()
    }
  }
  window.addEventListener('storage', onStorageAcknowledgement)
  channel?.postMessage(msg)
  // Most reliable path: the `storage` event fires in the original tab even when
  // the cross-origin provider round-trip severed window.opener.
  writeOAuthResultToStorage(msg)
  // Third path — signal the opener directly, in case the others are unavailable.
  try {
    window.opener?.postMessage({ __integrationOAuth: true, ...msg }, window.location.origin)
  } catch {
    // cross-origin / no opener — the other channels still apply.
  }

  // Do not close until the original app acknowledges receipt. If every
  // cross-tab signal is deferred or dropped, keep this tab alive and complete
  // the flow here instead of leaving the user on a stale provider page.
  const fallbackTimer = window.setTimeout(() => {
    if (closeTimer !== undefined) window.clearTimeout(closeTimer)
    window.removeEventListener('storage', onStorageAcknowledgement)
    channel?.close()
    navigateInPlace(oauthDestinationPath(msg))
  }, CLOSE_FALLBACK_MS)

  const root = document.getElementById('root')
  if (root) {
    root.innerHTML =
      '<div style="min-height:60vh;display:flex;flex-direction:column;align-items:center;' +
      'justify-content:center;gap:8px;font-family:system-ui,-apple-system,sans-serif;color:#6b7280">' +
      '<div style="font-size:15px">Finishing connection…</div>' +
      '<div style="font-size:13px;opacity:.7">You can close this tab.</div></div>'
  }
}
